import { useEffect, useRef } from 'react';
import { createActorContext } from '@xstate/react';
import { chatMachine } from './chatMachine';
import { getPersistedContext, setPersistedContext } from './aiContextStorage';

export const ChatMachineContext = createActorContext(chatMachine);

const HydrationWrapper = ({ children }: { children: React.ReactNode }) => {
    const actorRef = ChatMachineContext.useActorRef();
    const prevContextRef = useRef<string>('');
    const prevKeysRef = useRef<string>('');

    // On mount: hydrate from storage
    useEffect(() => {
        const persisted = getPersistedContext();
        if (Object.keys(persisted).length > 0) {
            actorRef.send({ type: 'HYDRATE_PERSISTED_CONTEXT', context: persisted });
        }
    }, [actorRef]);

    // Subscribe to state changes, persist when persistedContextKeys changes
    useEffect(() => {
        const sub = actorRef.subscribe((state) => {
            const { aiContext, persistedContextKeys } = state.context;
            if (persistedContextKeys.length === 0) return;

            const contextJson = JSON.stringify(aiContext);
            const keysJson = JSON.stringify(persistedContextKeys);

            if (contextJson !== prevContextRef.current || keysJson !== prevKeysRef.current) {
                prevContextRef.current = contextJson;
                prevKeysRef.current = keysJson;
                setPersistedContext(aiContext, persistedContextKeys);
            }
        });
        return () => sub.unsubscribe();
    }, [actorRef]);

    return children;
};

export const ChatMachineProvider = ({ children }: { children: React.ReactNode }) => {
    return (
        <ChatMachineContext.Provider>
            <HydrationWrapper>{children}</HydrationWrapper>
        </ChatMachineContext.Provider>
    );
};
